// Every tab is a peer with its own copy of the page. Sync asks a transport
// only to carry bytes, so the browser's channel between tabs, a test's
// unreliable one and, later, your.os between devices are interchangeable.
// meets: together/tabs, together/any-transport, together/converge
import { VersionVector, type LoroDoc } from "loro-crdt";

export interface Transport {
  send(bytes: Uint8Array): void;
  listen(receive: (bytes: Uint8Array) => void): void;
  close(): void;
}

// Tabs of one site, and nowhere else.
export class ChannelTransport implements Transport {
  private ch: BroadcastChannel;
  constructor(name: string) {
    this.ch = new BroadcastChannel(name);
  }
  send(bytes: Uint8Array) {
    this.ch.postMessage(bytes);
  }
  listen(receive: (bytes: Uint8Array) => void) {
    this.ch.onmessage = (e) => receive(new Uint8Array(e.data));
  }
  close() {
    this.ch.close();
  }
}

// Several transports as one: what is sent goes on each, what comes on any
// is received. A file's page talks to this browser's other tabs and, through
// the machine the file is on, to everyone else with it open.
export class FanTransport implements Transport {
  constructor(private all: Transport[]) {}
  send(bytes: Uint8Array) {
    for (const t of this.all) t.send(bytes);
  }
  listen(receive: (bytes: Uint8Array) => void) {
    for (const t of this.all) t.listen(receive);
  }
  close() {
    for (const t of this.all) t.close();
  }
}

// The network at its worst, for tests: each message may be dropped,
// duplicated, or delayed past the ones sent after it.
export class ChaosTransport implements Transport {
  private rand: () => number;
  private timers = new Set<ReturnType<typeof setTimeout>>();
  constructor(private inner: Transport, private opts = { drop: 0.2, duplicate: 0.2, maxDelay: 120, seed: 1 }) {
    let s = opts.seed >>> 0 || 1;
    this.rand = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 2 ** 32);
  }
  send(bytes: Uint8Array) {
    if (this.rand() < this.opts.drop) return;
    const copies = this.rand() < this.opts.duplicate ? 2 : 1;
    for (let i = 0; i < copies; i++) {
      const t = setTimeout(() => (this.timers.delete(t), this.inner.send(bytes)), this.rand() * this.opts.maxDelay);
      this.timers.add(t);
    }
  }
  listen(receive: (bytes: Uint8Array) => void) {
    this.inner.listen(receive);
  }
  close() {
    this.timers.forEach(clearTimeout);
    this.inner.close();
  }
}

const HELLO = 1; // here is my version; tell me what I lack, and I'll do the same
const VERSION = 2; // here is my version; send what I lack
const UPDATE = 3; // operations
export const PRESENCE = 4; // who is here and where (presence.ts)

export class Sync {
  private stop: () => void;
  private timer: ReturnType<typeof setInterval>;
  private others = new Map<number, (bytes: Uint8Array) => void>();

  // `every` is how often a version goes out regardless, which is what
  // recovers an update a transport lost when nothing comes after it.
  constructor(private doc: LoroDoc, private transport: Transport, every = 1000) {
    transport.listen((b) => this.receive(b));
    this.stop = doc.subscribeLocalUpdates((u) => this.send(UPDATE, u));
    this.send(HELLO, doc.oplogVersion().encode());
    this.timer = setInterval(() => this.send(VERSION, doc.oplogVersion().encode()), every);
  }

  // Another kind of message over the same transport (presence).
  on(kind: number, receive: (bytes: Uint8Array) => void) {
    this.others.set(kind, receive);
  }

  // Cut off: nothing goes out or comes in. Back on, a hello catches up
  // both ways, as a tab that opens late does.
  get offline() {
    return this.cut;
  }
  set offline(v: boolean) {
    this.cut = v;
    if (!v) this.send(HELLO, this.doc.oplogVersion().encode());
  }
  private cut = false;

  send(kind: number, body: Uint8Array) {
    if (this.cut) return;
    const msg = new Uint8Array(body.length + 1);
    msg[0] = kind;
    msg.set(body, 1);
    this.transport.send(msg);
  }

  // Operations this tab imported rather than wrote (the AI's, a source edit
  // made on a fork, a file): send them on as if they were its own.
  push(update: Uint8Array) {
    this.send(UPDATE, update);
  }

  close() {
    clearInterval(this.timer);
    this.stop();
    this.transport.close();
  }

  private receive(msg: Uint8Array) {
    if (this.cut) return;
    const kind = msg[0];
    const body = msg.subarray(1);
    if (kind === UPDATE) {
      const status = this.doc.import(body);
      // A gap: something before this update never arrived. Ask for it.
      if (status.pending) this.send(VERSION, this.doc.oplogVersion().encode());
    } else if (kind === HELLO || kind === VERSION) {
      let theirs: VersionVector;
      try {
        theirs = VersionVector.decode(body);
      } catch {
        return;
      }
      const mine = this.doc.oplogVersion();
      const order = mine.compare(theirs); // 1: I'm ahead, -1: behind, undefined: both
      if (order === 1 || order === undefined) this.send(UPDATE, this.doc.export({ mode: "update", from: theirs }));
      if (order === -1 || order === undefined || kind === HELLO) this.send(VERSION, mine.encode());
    } else this.others.get(kind)?.(body);
  }
}
